import type { RichTextField } from '@prismicio/client';
import { isFilled } from '@prismicio/client';
import { PrismicNextImage } from '@prismicio/next';
import { PrismicRichText } from '@prismicio/react';
import { OutlineCtaLink } from '@/components/common/OutlineCtaLink';
import { HorizontalContentCard } from '@/components/redesign/HorizontalContentCards';
import { ArrowLine, stripArrowGlyph } from './ArrowLine';
import { StepBody } from './StepBody';
import { StepTitle } from './StepTitle';
import type { NumberedIconStep } from './types';

export type { NumberedIconStep } from './types';
export { ArrowLine, stripArrowGlyph } from './ArrowLine';
export { StepBody } from './StepBody';
export { StepTitle } from './StepTitle';

// Icons that fit inside this square are rendered at this size — small assets
// get upscaled to a consistent 66×66 so cards line up cleanly.
const ICON_BASE_SIZE_PX = 66;
// Extra room granted, in one 16px step, when the uploaded icon's own
// dimensions exceed `ICON_BASE_SIZE_PX` on either axis.
const ICON_GROWTH_STEP_PX = 14;

type Props = {
  step: NumberedIconStep;
  index: number;
  /**
   * Extra classes for the outlined CTA link. Layouts that give cards room to
   * grow (e.g. a horizontal scroll rail) pass `whitespace-nowrap` here so a
   * long label expands the card rather than wrapping onto a second line.
   * Grid layouts leave this empty to keep the button on one column width.
   */
  ctaClassName?: string;
  /**
   * Plain-text override for the numbered badge.
   *   • string  → renders verbatim in place of the auto-generated `01`/`02`/…
   *   • undefined (or empty string) → auto-generate from `index` (default).
   *   • null → hide the badge entirely (opt-in; used by
   *     `SideHeaderCardsRow` default when the editor leaves the override blank).
   */
  numberLabel?: string | null;
  /**
   * Optional rich-text footnote rendered below the card body as a small
   * uppercase caption (14px / 120% / 400 / Inter, matching the Figma spec).
   * Only the `redesignCardsHorizontal` variation passes it today — other
   * callers omit it and no footnote is rendered.
   */
  footnote?: RichTextField | null;
};

/**
 * Numbered content card with a green icon tile, eyebrow, title, body, and an
 * arrow CTA pinned to the bottom. Originally the horizontal-cards layout of
 * `HeadlineDescriptionCtaSteps` — lifted here so any slice whose group matches
 * `NumberedIconStep` can render the same card.
 *
 * Link presence decides the CTA treatment (link wins over label-only):
 *   • link set  → outlined pill button (primary treatment).
 *   • no link, label set → plain arrow-prefixed text (secondary treatment).
 *   • neither → no CTA.
 * The display text prefers `step_cta_label`, then falls back to the link's own
 * text for legacy content.
 */
export function NumberedIconStepCard({
  step,
  index,
  ctaClassName,
  numberLabel,
  footnote,
}: Props) {
  const hasLink = isFilled.link(step.step_cta);
  const ctaLabel = stripArrowGlyph(
    step.step_cta_label || (step.step_cta as { text?: string }).text || '',
  );
  // `null` is the explicit opt-out — hide the badge. `undefined`/empty falls
  // back to the position-based `01`/`02`/… so existing callers keep their
  // auto-generated label.
  const hideNumber = numberLabel === null;
  const displayNumber =
    numberLabel && numberLabel.trim().length > 0
      ? numberLabel
      : String(index + 1).padStart(2, '0');

  const hasIcon = isFilled.image(step.step_icon);
  const iconDims = hasIcon ? step.step_icon.dimensions : null;
  const iconOverflowsBase =
    !!iconDims &&
    (iconDims.width > ICON_BASE_SIZE_PX || iconDims.height > ICON_BASE_SIZE_PX);
  const iconTileSize = iconOverflowsBase
    ? ICON_BASE_SIZE_PX + ICON_GROWTH_STEP_PX
    : ICON_BASE_SIZE_PX;

  return (
    <HorizontalContentCard>
      <div
        className="flex items-center justify-between"
        style={{ minHeight: iconTileSize }}
      >
        {hideNumber ? (
          <span aria-hidden="true" />
        ) : (
          <span className="border-l border-[#4b0fea] pl-3 text-[18px] font-medium leading-[1.5] text-[#444]">
            {displayNumber}
          </span>
        )}
        {hasIcon && (
          <div
            className="relative shrink-0"
            style={{ width: iconTileSize, height: iconTileSize }}
          >
            <PrismicNextImage
              field={step.step_icon}
              fill
              sizes={`${iconTileSize}px`}
              className="object-contain"
              fallbackAlt=""
            />
          </div>
        )}
      </div>

      <div className="flex flex-col gap-4">
        <div>
          {step.step_label && (
            <p className="text-[14px] font-normal leading-[1.5] text-[#444]">
              {step.step_label}
            </p>
          )}
          <StepTitle
            field={step.step_title}
            className="text-[22px] font-semibold leading-[1.5] text-[#041118]"
          />
        </div>

        <StepBody
          field={step.step_description}
          className="text-[16px] font-light leading-[1.5] text-[#041118] md:text-[18px]"
        />

        {isFilled.richText(footnote) && (
          <PrismicRichText
            field={footnote}
            components={{
              // Base paragraph mirrors the Figma "Body small" spec:
              // Inter · 14 · 400 · 120% · uppercase · dark near-black. Strong
              // spans keep the same size but bump to semibold so editors can
              // emphasise a word without breaking the caption rhythm.
              paragraph: ({ children }) => (
                <p className="text-[14px] font-normal uppercase leading-[1.2] tracking-[0] text-[#041118]">
                  {children}
                </p>
              ),
              strong: ({ children }) => (
                <strong className="font-semibold">{children}</strong>
              ),
            }}
          />
        )}
      </div>

      {hasLink ? (
        <div className="mt-auto pt-2">
          {/* Cards are always light-surfaced (`bg-[#f3f3f3]`) regardless of the
              section theme, so the pill stays on the light tone. */}
          <OutlineCtaLink
            field={step.step_cta}
            label={ctaLabel || undefined}
            tone="light"
            className={ctaClassName}
          />
        </div>
      ) : ctaLabel ? (
        <div className="mt-auto pt-2">
          <ArrowLine
            // `leading-*` must come after the font size: tailwind-merge treats
            // `text-[size]` as conflicting with `leading-*` (because of the
            // `text-base/7` shorthand) and would drop an earlier line-height.
            className="items-center font-medium text-[#041118] text-[18px] leading-[1.5]"
            iconClassName="h-[18px] w-[18px]"
          >
            <span>{ctaLabel}</span>
          </ArrowLine>
        </div>
      ) : null}
    </HorizontalContentCard>
  );
}
